import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/api';
import {
  LayoutDashboard, Server, Grid3X3, FileText, Activity,
  Settings, Users, DollarSign, LogOut, Package, Monitor, AlertTriangle, TrendingUp
} from 'lucide-react';

const NAV = [
  { label: 'Overview', items: [
    { path: '/', icon: LayoutDashboard, text: 'Dashboard' },
    { path: '/revenue', icon: TrendingUp, text: 'Revenue' },
    { path: '/pricing', icon: DollarSign, text: 'Pricing' },
  ]},
  { label: 'Operations', items: [
    { path: '/devices', icon: Server, text: 'Devices' },
    { path: '/lockers', icon: Grid3X3, text: 'Locker Boxes' },
    { path: '/live-lockers', icon: Monitor, text: 'Live Lockers' },
    { path: '/parcels', icon: Package, text: 'Parcels' },
    { path: '/alerts', icon: AlertTriangle, text: 'Alerts' },
    { path: '/logs', icon: Activity, text: 'Activity Log' },
  ]},
  { label: 'People', items: [
    { path: '/users', icon: Users, text: 'Users' },
  ]},
];

// Number of problems needing attention (errors + critical), shown next to Alerts.
function useAlertCount() {
  const [n, setN] = useState(0);
  useEffect(() => {
    let stop = false;
    const load = async () => {
      if (document.hidden) return;
      try {
        const r = await adminAPI.alerts();
        if (!stop) setN((r.data.alerts || []).filter(a => a.severity === 'critical' || a.severity === 'error').length);
      } catch (e) { /* keep last value */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { stop = true; clearInterval(t); };
  }, []);
  return n;
}

export default function Sidebar({ mobile = false, open = true, onClose = () => {} }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const alertCount = useAlertCount();

  if (mobile && !open) return null;
  const go = (path) => { navigate(path); if (mobile) onClose(); };

  return (
    <>
    {mobile && <div style={s.overlay} onClick={onClose} />}
    <aside style={{ ...s.sidebar, ...(mobile ? { width: 270, boxShadow: '0 0 40px rgba(0,0,0,0.6)' } : {}) }}>
      <div style={s.brand}>
        <img src="/damitove-logo.png" alt="Damitove" style={{height: 32, width: "auto"}} />
        <div style={s.brandText}>Dami<span style={{ color: '#8BC63E' }}>tove</span></div>
      </div>

      <nav style={s.nav}>
        {NAV.map((section) => (
          <div key={section.label}>
            <div style={s.section}>{section.label}</div>
            {section.items.map((item) => {
              const active = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <div
                  key={item.path}
                  style={{ ...s.item, ...(active ? s.itemActive : {}) }}
                  onClick={() => go(item.path)}
                  role="link" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') go(item.path); }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#161821'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <Icon size={18} />
                  <span>{item.text}</span>
                  {item.path === '/alerts' && alertCount > 0 && <span style={s.badge}>{alertCount}</span>}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      <div style={s.footer}>
        <div style={s.userInfo}>
          <div style={s.avatar}>{user?.full_name?.[0] || 'A'}</div>
          <div>
            <div style={s.userName}>{user?.full_name || 'Admin'}</div>
            <div style={s.userRole}>{user?.role}</div>
          </div>
        </div>
        <div
          style={s.logoutBtn}
          onClick={logout}
          onMouseEnter={(e) => e.currentTarget.style.color = '#ff4466'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#8a8da4'}
        >
          <LogOut size={16} /> Sign out
        </div>
      </div>
    </aside>
    </>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 99 },
  badge: { marginLeft: 'auto', background: '#ff4466', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px' },
  sidebar: {
    width: 250, background: '#0f1117', borderRight: '1px solid #252733',
    display: 'flex', flexDirection: 'column', position: 'fixed',
    top: 0, left: 0, bottom: 0, zIndex: 100,
  },
  brand: {
    padding: '20px 20px 18px', borderBottom: '1px solid #252733',
    display: 'flex', alignItems: 'center', gap: 10,
  },
  brandIcon: {
    width: 32, height: 32, background: 'linear-gradient(135deg, #8BC63E, #00b4d8)',
    borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: 15, color: '#07080c',
  },
  brandText: { fontWeight: 700, fontSize: 17, color: '#e4e5ed', letterSpacing: -0.3 },
  nav: { flex: 1, padding: '12px 10px', overflowY: 'auto' },
  section: {
    padding: '8px 12px 4px', fontSize: 10, letterSpacing: 1.2,
    textTransform: 'uppercase', color: '#8a8da4', marginTop: 10,
  },
  item: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
    borderRadius: 8, cursor: 'pointer', color: '#8789a0', fontSize: 13,
    fontWeight: 500, transition: 'all 0.15s',
  },
  itemActive: {
    background: 'rgba(139,198,62,0.1)', color: '#8BC63E',
  },
  footer: {
    padding: '14px 16px', borderTop: '1px solid #252733',
  },
  userInfo: {
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
  },
  avatar: {
    width: 32, height: 32, borderRadius: 8, background: '#1e2029',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, color: '#8BC63E',
  },
  userName: { fontSize: 13, fontWeight: 600, color: '#e4e5ed' },
  userRole: { fontSize: 11, color: '#8a8da4', textTransform: 'capitalize' },
  logoutBtn: {
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
    color: '#8a8da4', cursor: 'pointer', padding: '6px 0', transition: 'color 0.15s',
  },
};

